export interface MapViewState {
  latitude: number;
  longitude: number;
  zoom: number;
}

export interface MapBounds {
  sw: { lat: number; lng: number };
  ne: { lat: number; lng: number };
}

export interface MapService {
  calculateBounds(center: { lat: number; lng: number }, zoom: number): MapBounds;
  validateCoordinates(lat: number, lng: number): boolean;
  calculateDistance(point1: { lat: number; lng: number }, point2: { lat: number; lng: number }): number;
  isPointInBounds(point: { lat: number; lng: number }, bounds: MapBounds): boolean;
}

export class MapboxMapService implements MapService {
  private static instance: MapboxMapService;

  private constructor () {}

  public static getInstance (): MapboxMapService {
    if (!MapboxMapService.instance) {
      MapboxMapService.instance = new MapboxMapService();
    }
    return MapboxMapService.instance;
  }

  calculateBounds (center: { lat: number; lng: number }, zoom: number): MapBounds {
    // تقريب لحساب bounds بناءً على المركز والزوم
    const latDelta = 180 / Math.pow(2, zoom);
    const lngDelta = 360 / Math.pow(2, zoom);

    return {
      sw: {
        lat: center.lat - latDelta / 2,
        lng: center.lng - lngDelta / 2
      },
      ne: {
        lat: center.lat + latDelta / 2,
        lng: center.lng + lngDelta / 2
      }
    };
  }

  validateCoordinates (lat: number, lng: number): boolean {
    return (
      lat >= -90 && lat <= 90 &&
      lng >= -180 && lng <= 180 &&
      !isNaN(lat) && !isNaN(lng)
    );
  }

  // Utility functions for coordinate validation
  isValidLatitude (value: any): boolean {
    return value !== undefined &&
           value !== null &&
           !isNaN(value) &&
           typeof value === 'number' &&
           value >= -90 &&
           value <= 90;
  }

  isValidLongitude (value: any): boolean {
    return value !== undefined &&
           value !== null &&
           !isNaN(value) &&
           typeof value === 'number' &&
           value >= -180 &&
           value <= 180;
  }

  isValidCoordinatePair (lat: any, lng: any): boolean {
    return this.isValidLatitude(lat) && this.isValidLongitude(lng);
  }

  sanitizeCoordinates (lat: any, lng: any): { lat: number; lng: number } {
    return {
      lat: this.isValidLatitude(lat) ? lat : 0,
      lng: this.isValidLongitude(lng) ? lng : 0
    };
  }

  calculateDistance (
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const R = 6371; // نصف قطر الأرض بالكيلومتر
    const dLat = this.toRadians(point2.lat - point1.lat);
    const dLng = this.toRadians(point2.lng - point1.lng);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(point1.lat)) *
        Math.cos(this.toRadians(point2.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  isPointInBounds (point: { lat: number; lng: number }, bounds: MapBounds): boolean {
    return (
      point.lat >= bounds.sw.lat &&
      point.lat <= bounds.ne.lat &&
      point.lng >= bounds.sw.lng &&
      point.lng <= bounds.ne.lng
    );
  }

  private toRadians (degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}
